import type { PushResult } from '../core/types'
import { fetchLocalDocs, updateLocalDocs } from '../core/operations'
// import { Network } from '@capacitor/network';
import ky from 'ky'

export class ReplicationEngine {
	// Размеры чанка
	private readonly CHUNK_SIZE_PUSH = 200
	private readonly CHUNK_SIZE_PULL = 500	

	// Объект с состояниями репликации
	replicationStates = {
		// Дефолтные значения
		isOnline: false,
		lastSuccessSync: 0,
		dbName: R.env.dbName,
		queryURL: '',
		isProcessing: false, // Флаг о том, что выполняется процесс
		skipDocs: {} as { [key: string]: string[] },
	}

	// Приватный конструктор - создавать класс можно только через create
	private constructor(queryURL: string) {
		this.replicationStates.queryURL = queryURL
	}

	// Статический метод для создания экземпляра
	static async create(): Promise<ReplicationEngine> {
		try {
			// Получим ссылку для подключения к Kuzzle
			const queryURL =
				R.libs.Kuzzle?.host === 'localhost'
					? `http://${R.libs.Kuzzle?.host}:${R.libs.Kuzzle?.port}/_query`
					: `https://${R.libs.Kuzzle?.host}:${R.libs.Kuzzle?.port}/_query`

			console.log('queryURL', queryURL)

			// Создаем и возвращаем класс, так как вызываем private constructor(kuzzle: any) - см выше
			return new ReplicationEngine(queryURL)
		} catch (e) {
			if (e instanceof Error) {
				console.error('Failed to create ReplicationEngine:', e.message)
				R.libs.mantine.MantineError?.('Системная ошибка!', `Failed to create ReplicationEngine: ${e.message}`)
			} else {
				console.error('Failed to create ReplicationEngine:', String(e))
				R.libs.mantine.MantineError?.('Системная ошибка!', `Failed to create ReplicationEngine: ${e}`)
			}
			throw e
		}
	}

	// Метод для проверки и обновляения статуса подключения
	// async checkConnectionState() {
	// 	const connectionState = await Network.getStatus();

	// 	// // Если статус сменился, то обновляем
	// 	// if (connectionState?.connected !== this.replicationStates.isOnline) {
	// 	// 	// Меняем на новый
	// 	// 	this.replicationStates.isOnline = connectionState?.connected;
	// 	// }
	// }

	async getLastSuccessSync() {
		// Получаем время последней синхронизации из локальной базы
		const lastSuccessSyncData = await fetchLocalDocs({
			dbClass: 'localParams',
			filters: { equals: { id: 'lastSuccessSync' } },
			size: 1,
		})

		this.replicationStates.lastSuccessSync = lastSuccessSyncData?.[0]?.value ? Number(lastSuccessSyncData[0].value) : 0
	}

	getChunksByArr(items: any, chunkSize: number = this.CHUNK_SIZE_PUSH) {
		// Нарезаем массив на массив массивов меньшей длины
		const chunksWithItems = []

		// For работает быстрее, чем reduce и Array.from, поэтому будем делать через него
		for (let i = 0; i < items.length; i += chunkSize) {
			chunksWithItems.push(items.slice(i, i + chunkSize))
		}

		return chunksWithItems
	}

	getChunkParams(totalCount: number, chunkSize: number = this.CHUNK_SIZE_PULL) {
		// На основании числа документов и размера чанка генерируем массив объектов с from и size
		const chunkParams = []

		// Делим число документов на размер, получая количество чанков
		const chunkCount = totalCount / chunkSize

		if (chunkCount <= 1) {
			chunkParams.push({
				from: 0,
				size: chunkSize,
			})
		} else {
			for (let i = 0; i < chunkCount; i++) {
				chunkParams.push({
					from: i * chunkSize,
					size: (i + 1) * chunkSize < totalCount ? (i + 1) * chunkSize : totalCount - i * chunkSize,
				})
			}
		}

		return chunkParams
	}

	conflictHandler(localDoc: any, serverDoc: any, kuzzleTimeNow: number) {
		// Принимает 3 параметра - локальный и серверный документы, текущее время на сервере
		// Пока так - создаем новый документ на основе локального, с более свежим временем обновления
		if (localDoc?.sysinfo) localDoc.sysinfo.updatedAt = kuzzleTimeNow

		// И добавляем флаг о том, что уже исправлено, чтобы не зацикливать логику на сервере
		localDoc.fixed = true
		return localDoc
	}

	async pushHandler() {
		// Получаем названия классов, указанных для синхронизации.
		const syncCollections = R.localData?.syncCollections

		// Проходимся по каждой коллекции, получаем документы и отправляем их на синхронизацию
		if (navigator.onLine && syncCollections && R.db.states.auth.token) {
			try {
				// Дожидаемся, пока завершатся остальные функции
				await new Promise((resolve) => {
					const interval = setInterval(async () => {
						if (!this.replicationStates.isProcessing) {
							clearInterval(interval)
							resolve(undefined)
						}
					}, 50) // Опрашиваем каждые 50 мс
				})

				// Устанавливаем флаг о том, что репликация занята процессом
				this.replicationStates.isProcessing = true

				// for (const iCollectionName of syncCollections) {
				await Promise.all(
					syncCollections?.map(async (iCollectionName: string) => {
						// Пропускаем коллекцию с локальными параметрами
						if (['localParams', 'localFile'].includes(iCollectionName)) {
							return Promise.resolve()
						}

						const localItems = await fetchLocalDocs({
							dbClass: iCollectionName,
							filters: {
								or: [
									{ range: { 'sysinfo.createdAt': { gte: this.replicationStates.lastSuccessSync } } },
									{ range: { 'sysinfo.updatedAt': { gte: this.replicationStates.lastSuccessSync } } },

									// { 'sysinfo.createdAt': { $gt: this.replicationStates.lastSuccessSync } },
									// { 'sysinfo.updatedAt': { $gt: this.replicationStates.lastSuccessSync } },
								],
							},
						})

						const items = []

						this.replicationStates.skipDocs[iCollectionName] = []

						console.log(`У коллекции ${iCollectionName} найдено ${localItems?.length} записей для репликации!`)

						// Отделяем от items локальные поля
						for (const item of localItems) {
							// Добавлем id и классы документов в массив исключений для pullHandler - зачем? Я забыл и теперь косячит! БАГ
							// this.replicationStates.skipDocs[iCollectionName].push(item.id)
							// Деструктурируем объект, чтобы убрать локальные поля
							const { _deleted, _rev, _meta, _attachments, localFeatures, dbClass, fixed, ...otherFeatures } = item

							items.push(otherFeatures)
						}

						console.log(`У коллекции ${iCollectionName} документы`, items)

						if (items.length) {
							// Нарезаем массив на чанки, чтобы не превышать лемиты передачи данных
							const chunksWithItems = this.getChunksByArr(items, 500)

							// console.log(`У коллекции ${iCollectionName} chunksWithItems`, chunksWithItems)

							// Отправляем полученные документы на сервер и обрабатываем ответ
							await Promise.all(
								chunksWithItems.map(async (iChunk: any) => {
									// console.log(`У коллекции ${iCollectionName} iChunk`, iChunk)

									try {
										// Так как K.query не получается задать POST метод, хотя так и описано в документации, используем ky
										const request: { result: PushResult } = await ky
											.post(this.replicationStates.queryURL, {
												// Используем json вместо body для автоматической сериализации
												json: {
													// JSON Web Token - JWT - Получаем его при авторизации в Kuzzle и используем для подписи запросов http
													// В R.db.states.auth.token вседа актуальный токен
													jwt: R.db.states.auth.token,
													controller: 'rolder', // обязательные поля для Kuzzle
													action: 'pushData',
													dbName: this.replicationStates.dbName,
													dbClass: iCollectionName,
													lastSuccessSync: this.replicationStates.lastSuccessSync,
													items: iChunk,
												},
												timeout: 100000,
											})
											.json()

										const pushResult: PushResult = request.result

										if (pushResult.errorDocs?.length) {
											console.log(`У коллекции ${iCollectionName} ОШИБКИ ${pushResult.errorDocs}!`)
											for (const kuzzleError of pushResult.errorDocs) {
												log.error('pushHandler error.', kuzzleError?.reason)
												R.libs.mantine.MantineError?.('Системная ошибка!', `pushHandler error: ${kuzzleError?.reason}`)
											}
										}

										if (pushResult.conflictDocs?.length) {
											console.log(`У коллекции ${iCollectionName} конфликты ${pushResult.conflictDocs}!`)
										}

										// console.log(`У коллекции ${iCollectionName} request`, request)
										// console.log(`У коллекции ${iCollectionName} результат`, pushResult)

										// Отловим ошибки.
										// Проверяем массив конфликтов
										if (pushResult?.conflictDocs?.length) {
											// Перебираем конфликтные записи, разруливаем конфликты и отправляем повторно
											const fixedDocs = []
											for (const iConflictDocs of pushResult.conflictDocs) {
												fixedDocs.push(
													this.conflictHandler(iConflictDocs.localDoc, iConflictDocs.serverDoc, pushResult.kuzzleTimeNow)
												)
											}

											// Отправлем исправленные документы на сервер
											await ky
												.post(this.replicationStates.queryURL, {
													// Используем json вместо body для автоматической сериализации
													json: {
														// JSON Web Token - JWT - Получаем его при авторизации в Kuzzle и используем для подписи запросов http
														// В R.db.states.auth.token вседа актуальный токен
														jwt: R.db.states.auth.token,
														controller: 'rolder',
														action: 'pushData',
														dbName: this.replicationStates.dbName,
														dbClass: iCollectionName,
														lastSuccessSync: this.replicationStates.lastSuccessSync,
														items: fixedDocs,
													},
													timeout: 100000,
												})
												.json()

											// И обновляем их в локальной базе
											await R.localData.updateLocalDocs({
												dbClass: iCollectionName,
												items: fixedDocs,
											})
										}
										return pushResult
									} catch (e: any) {
										// Устанавливаем флаг о том, что репликация теперь не занята процессом
										this.replicationStates.isProcessing = false
										log.error('pushHandler error.', e)
										R.libs.mantine.MantineError?.('Системная ошибка!', `pushHandler error: ${e.message}`)
										return
									}
								})
							)
						}

						return items
					})
				)
			} catch (e: any) {
				this.replicationStates.isProcessing = false
				log.error('pushHandler error.', e)
				R.libs.mantine.MantineError?.('Системная ошибка!', `pushHandler error: ${e.message}`)
				return
			}

			// Устанавливаем флаг о том, что репликация теперь не занята процессом
			this.replicationStates.isProcessing = false
		}
	}

	async pullHandler() {
		// Получаем названия классов, указанных для синхронизации.
		const syncCollections = R.localData?.syncCollections

		// Проходимся по каждой коллекции, и получаем для них документы после this.replicationStates.lastSuccessSync
		if (navigator.onLine && syncCollections && R.db.states.auth.token) {
			// Дожидаемся, пока завершатся остальные функции
			await new Promise((resolve) => {
				const interval = setInterval(async () => {
					if (!this.replicationStates.isProcessing) {
						clearInterval(interval)
						resolve(undefined)
					}
				}, 50) // Опрашиваем каждые 50 мс
			})

			// Устанавливаем флаг о том, что репликация занята процессом
			this.replicationStates.isProcessing = true

			await Promise.all(
				syncCollections?.map(async (iCollectionName: string) => {
					// Пропускаем коллекцию с локальными параметрами
					if (['localParams', 'localFile'].includes(iCollectionName)) {
						return Promise.resolve()
					}

					console.log('\n\n\n\n\n\n\n\nR.db.states.auth.token', R.db.states.auth.token)
					// console.log(
					// 	JSON.stringify({
					// 		// JSON Web Token - JWT - Получаем его при авторизации в Kuzzle и используем для подписи запросов http
					// 		// В R.db.states.auth.token вседа актуальный токен
					// 		jwt: R.db.states.auth.token,
					// 		controller: 'rolder',
					// 		action: 'getData',
					// 		dbName: this.replicationStates.dbName,
					// 		dbClass: iCollectionName,
					// 		lastSuccessSync: this.replicationStates.lastSuccessSync,
					// 		from: 0,
					// 		size: 0,
					// 		skipDocs: this.replicationStates.skipDocs,
					// 	})
					// );

					try {
						// Получаем число документов для данного класса
						const docsCount: { result: { total: number } } = await ky
							.post(this.replicationStates.queryURL, {
								// Используем json вместо body для автоматической сериализации
								json: {
									// JSON Web Token - JWT - Получаем его при авторизации в Kuzzle и используем для подписи запросов http
									// В R.db.states.auth.token вседа актуальный токен
									jwt: R.db.states.auth.token,
									controller: 'rolder',
									action: 'getData',
									dbName: this.replicationStates.dbName,
									dbClass: iCollectionName,
									lastSuccessSync: this.replicationStates.lastSuccessSync,
									from: 0,
									size: 0,
									skipDocs: this.replicationStates.skipDocs,
								},
								timeout: 100000,
							})
							.json()

						const countResult = docsCount?.result || {}

						console.log(`Для коллекции ${iCollectionName} получено ${countResult.total} документов.`)

						// Если документы не получены, то завершаем промис
						if (countResult.total === 0) {
							return Promise.resolve()
						}

						// Получаем массив параметров, для чанкованного получения данных
						const chunkParams = this.getChunkParams(countResult.total, this.CHUNK_SIZE_PULL)

						// Перебираем все параметры и создаем записи в локальной базе
						await Promise.all(
							chunkParams.map(async (iChunkParams) => {
								// Получаем документы для данной выборки
								const docsChunk: { result: { items: any[] } } = await ky
									.post(this.replicationStates.queryURL, {
										// Используем json вместо body для автоматической сериализации
										json: {
											// JSON Web Token - JWT - Получаем его при авторизации в Kuzzle и используем для подписи запросов http
											// В R.db.states.auth.token вседа актуальный токен
											jwt: R.db.states.auth.token,
											controller: 'rolder',
											action: 'getData',
											dbName: this.replicationStates.dbName,
											dbClass: iCollectionName,
											lastSuccessSync: this.replicationStates.lastSuccessSync,
											from: iChunkParams.from,
											size: iChunkParams.size,
											skipDocs: this.replicationStates.skipDocs,
										},
										timeout: 100000,
									})
									.json()

								const result = docsChunk?.result || {}

								// Перебираем полученные документы и создаем их в localData
								// При этом документы, которых ранее не было, создаются сами за счет bulk операций
								await updateLocalDocs({
									dbClass: iCollectionName,
									items: result.items,
								})

								return docsChunk
							})
						)
					} catch (e) {
						// Устанавливаем флаг о том, что репликация теперь не занята процессом
						this.replicationStates.isProcessing = false
						console.log(`Ошибка при получении данных для ${iCollectionName}`, e)
					}

					// this.replicationStates.lastSuccessSync;

					return iCollectionName
				})
			)

			// Устанавливаем флаг о том, что репликация теперь не занята процессом
			this.replicationStates.isProcessing = false

			// Очищаем массив документов для пропуска
			this.replicationStates.skipDocs = {}
		}
	}

	async pullStream() {
		// Получаем названия классов, указанных для синхронизации.
		const syncCollections = R.localData?.syncCollections

		// Передаем эти классы на создание подписки на сервер
		// Сервер будет слушать все документы в этих классах, и когда они изменяться, то будет отправлять обновления
		if (navigator.onLine && syncCollections && R.db.states.auth.token) {
			// Дожидаемся, пока завершатся остальные функции
			await new Promise((resolve) => {
				const interval = setInterval(async () => {
					if (!this.replicationStates.isProcessing) {
						clearInterval(interval)
						resolve(undefined)
					}
				}, 50) // Опрашиваем каждые 50 мс
			})

			// Получаем число документов для данного класса
			const serverSubscriptionReq: { result: any } = await ky
				.post(this.replicationStates.queryURL, {
					// Используем json вместо body для автоматической сериализации
					json: {
						// JSON Web Token - JWT - Получаем его при авторизации в Kuzzle и используем для подписи запросов http
						// В R.db.states.auth.token вседа актуальный токен
						jwt: R.db.states.auth.token,
						controller: 'rolder',
						action: 'streamData',
						dbName: this.replicationStates.dbName,
						subDbClasses: syncCollections.filter((fClassName: string) => !['localParams', 'localFile'].includes(fClassName)),
						lastSuccessSync: this.replicationStates.lastSuccessSync,
					},
					timeout: 100000,
				})
				.json()

			const serverSubscription = serverSubscriptionReq?.result

			const sse = new EventSource('this.replicationStates.queryURL')

			sse.onmessage = (event) => {
				// const { collection, data } = JSON.parse(event.data);
				console.log('Прилетело по подписке с сервера', {
					...JSON.parse(event.data),
				})
				// switch (collection) {
				// 	case 'users':
				// 		handleUsersUpdate(data);
				// 		break;
				// 	case 'orders':
				// 		handleOrdersUpdate(data);
				// 		break;
				// 	// ...
				// }
			}

			console.log('serverSubscription', serverSubscription)
		}
	}

	async finallySync() {
		if (navigator.onLine && R.db.states.auth.token) {
			// Дожидаемся, пока завершатся остальные функции
			await new Promise((resolve) => {
				const interval = setInterval(async () => {
					if (!this.replicationStates.isProcessing) {
						clearInterval(interval)
						resolve(undefined)
					}
				}, 50) // Опрашиваем каждые 50 мс
			})

			// Устанавливаем флаг о том, что репликация занята процессом
			this.replicationStates.isProcessing = true

			// Запрашиваем у Kuzzle его текущее время
			const kuzzleTimeNow: { result: number } = await ky
				.post(this.replicationStates.queryURL, {
					// Используем json вместо body для автоматической сериализации
					json: {
						// JSON Web Token - JWT - Получаем его при авторизации в Kuzzle и используем для подписи запросов http
						// В R.db.states.auth.token вседа актуальный токен
						jwt: R.db.states.auth.token,
						controller: 'rolder',
						action: 'getKuzzleTime',
					},
					timeout: 100000,
				})
				.json()

			// Обновляем время последней успешной синхронизации текущим временем от Kuzzle
			this.replicationStates.lastSuccessSync = kuzzleTimeNow.result

			// И обновляем его в локальной базе
			await updateLocalDocs({
				dbClass: 'localParams',
				items: [
					{
						id: 'lastSuccessSync',
						value: this.replicationStates.lastSuccessSync,
					},
				],
			})
		}

		// Устанавливаем флаг о том, что репликация теперь не занята процессом
		this.replicationStates.isProcessing = false

		return this.replicationStates.lastSuccessSync
	}
}
